/**
 *  @class
 *  @function SlideShow
 */
if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
      // Pre-bind methods to avoid creating functions in render loops
      this.videoPlay = this.videoPlay.bind(this);
      this.videoPause = this.videoPause.bind(this);
    }

    connectedCallback() {
      // Defer initialization to avoid blocking main thread during page load
      this._initTimeout = setTimeout(() => this.initialize(), 100);
    }

    disconnectedCallback() {
      if (this._initTimeout) {
        clearTimeout(this._initTimeout);
      }
      window.removeEventListener('resize.center_arrows', this._resizeHandler);
    }

    initialize() {
      const slideshow = this;
      const isMobile = window.matchMedia("(max-width: 768px)").matches;
      const isReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      
      // Get and parse data attributes once to avoid repeated access
      const slideshow_slides = Array.from(slideshow.querySelectorAll('.carousel__slide'));
      const autoplay = slideshow.dataset.autoplay === 'false' ? false : parseInt(slideshow.dataset.autoplay, 10);
      const align = slideshow.dataset.align === 'center' ? 'center' : 'left';
      const fade = slideshow.dataset.fade === 'true';
      const pageDots = slideshow.dataset.dots === 'true';
      const header = document.querySelector('theme-header');
      const custom_dots = slideshow.querySelector('.flickity-dots');
      const autoplay_progress = slideshow.querySelector('.slideshow--autoplay-progress');
      const progress_bar = slideshow.parentNode.querySelector('.flickity-progress--bar:not(.slideshow--autoplay-progress)');
      const prev_buttons = slideshow.querySelectorAll('.flickity-prev');
      const next_buttons = slideshow.querySelectorAll('.flickity-next');
      const rightToLeft = document.dir === 'rtl';
      
      // Only load GSAP if needed and available
      const animations_enabled = document.body.classList.contains('animations-true') && 
                                typeof gsap !== 'undefined' && 
                                !isReducedMotion && 
                                !isMobile; // Disable complex animations on mobile
      
      // Stored animation references
      this.animations = [];
      this.paused = false;

      // Prepare base Flickity options with optimized settings for mobile
      const args = {
        wrapAround: true,
        cellAlign: align,
        pageDots: pageDots,
        contain: true,
        fade: fade,
        autoPlay: autoplay,
        rightToLeft: rightToLeft,
        prevNextButtons: false,
        cellSelector: '.carousel__slide',
        imagesLoaded: true, // Important for proper cell sizing
        lazyLoad: 2, // Load adjacent images for better UX
        on: {}
      };

      // Optimize performance on mobile
      if (isMobile) {
        args.draggable = true;
        args.freeScroll = false;
        args.friction = 0.8; // Higher friction for smoother performance
        args.selectedAttraction = 0.12; // Lower attraction for smoother performance
      }

      if (custom_dots) {
        args.pageDots = false;
        args.pauseAutoPlayOnHover = false;
      }

      if (slideshow.dataset.adapt === 'true') {
        args.adaptiveHeight = true;
      }

      // Setup for main slideshow
      if (slideshow.classList.contains('main-slideshow')) {
        // Prepare animations but don't run them yet
        if (animations_enabled) {
          // We'll lazily initialize animations only when needed
          this.animationsReady = false;
        }

        args.on = {
          staticClick: function() {
            this.unpausePlayer();
          },
          ready: function() {
            let flkty = this;

            // Handle transparent header
            if (header?.classList.contains('transparent--true')) {
              const sectionIndex = slideshow.dataset.sectionIndex;
              let i = parseInt(sectionIndex, 10);

              if (Shopify.designMode) {
                const children = [...slideshow.closest('.shopify-section').parentNode.children];
                i = children.indexOf(slideshow.closest('.shopify-section')) + 1;
              }
              
              if (i === 1 && slideshow.classList.contains('section-spacing--disable-top')) {
                slideshow.classList.add('slideshow--top');
              }

              if (i === 1 && slideshow.classList.contains('change-header--true')) {
                const color_text = getComputedStyle(this.selectedElement).getPropertyValue('--color-text');
                header.style.setProperty('--color-header-transparent-text', color_text);
              }
            }

            // Initialize animations on first slide only when needed
            if (animations_enabled) {
              // Lazy load GSAP animations once content is ready
              slideshow.initAnimations().then(() => {
                slideshow.animateSlides(0, slideshow, slideshow.animations);
              });
            }

            // Handle font loading without blocking
            if ('fonts' in document) {
              document.fonts.ready.then(function() {
                requestAnimationFrame(() => flkty.resize());
              });
            } else {
              // Fallback for browsers without document.fonts
              setTimeout(() => flkty.resize(), 1000);
            }

            // Setup video on first slide
            const video_container = flkty.cells[0].element.querySelector('.slideshow__slide-video-bg');
            if (video_container) {
              if (video_container.querySelector('iframe')) {
                video_container.querySelector('iframe').onload = function() {
                  slideshow.videoPlay(video_container);
                };
              } else if (video_container.querySelector('video')) {
                video_container.querySelector('video').onloadstart = function() {
                  slideshow.videoPlay(video_container);
                };
              }
            }

            // Setup custom dots
            if (custom_dots) {
              const dots = custom_dots.querySelectorAll('li');
              dots.forEach((dot, i) => {
                dot.addEventListener('click', () => flkty.select(i));
              });
              dots[this.selectedIndex].classList.add('is-selected');
            }
          },
          change: function(index) {
            const flkty = this;
            const previousIndex = fizzyUIUtils.modulo(flkty.selectedIndex - 1, flkty.slides.length);

            // Only run animations if enabled and ready
            if (animations_enabled && slideshow.animationsReady) {
              slideshow.animateReverse(previousIndex, slideshow, slideshow.animations);
              slideshow.animateSlides(index, slideshow, slideshow.animations);
            }

            // Update colors
            const color_text = getComputedStyle(this.selectedElement).getPropertyValue('--color-text');
            const color_text_rgb = getComputedStyle(this.selectedElement).getPropertyValue('--color-text-rgb');
            
            slideshow.style.setProperty('--color-text', color_text);
            slideshow.style.setProperty('--color-text-rgb', color_text_rgb);

            // Update header if transparent
            if (header && header.classList.contains('transparent--true')) {
              let i = parseInt(slideshow.dataset.sectionIndex, 10);
              if (Shopify.designMode) {
                const children = [...slideshow.closest('.shopify-section').parentNode.children];
                i = children.indexOf(slideshow.closest('.shopify-section')) + 1;
              }
              if (i === 1 && slideshow.classList.contains('change-header--true')) {
                header.style.setProperty('--color-header-transparent-text', color_text);
              }
            }

            // Handle autoplay
            if (autoplay && flkty.player.state !== 'paused') {
              flkty.stopPlayer();
              flkty.playPlayer();
            }

            // Handle videos
            // Previous slide video - pause
            const video_container_prev = flkty.cells[previousIndex]?.element.querySelector('.slideshow__slide-video-bg');
            if (video_container_prev) {
              slideshow.videoPause(video_container_prev);
            }
            // Current slide video - play
            const video_container = flkty.cells[index]?.element.querySelector('.slideshow__slide-video-bg');
            if (video_container) {
              if (video_container.querySelector('iframe')) {
                if (video_container.querySelector('iframe').classList.contains('lazyload')) {
                  video_container.querySelector('iframe').addEventListener('lazybeforeunveil', () => {
                    slideshow.videoPlay(video_container);
                  });
                  if (typeof lazySizes !== 'undefined') {
                    lazySizes.loader.checkElems();
                  }
                } else {
                  slideshow.videoPlay(video_container);
                }
              } else if (video_container.querySelector('video')) {
                slideshow.videoPlay(video_container);
              }
            }

            // Update custom dots
            if (custom_dots) {
              const dots = custom_dots.querySelectorAll('li');
              dots.forEach(dot => dot.classList.remove('is-selected'));
              dots[this.selectedIndex].classList.add('is-selected');
            }
          }
        };

        // Handle adaptive height for image slides
        if (slideshow.classList.contains('desktop-height-image') || slideshow.classList.contains('mobile-height-image')) {
          args.adaptiveHeight = true;
        }
      }

      // Testimonials carousel
      if (slideshow.classList.contains('testimonials--carousel')) {
        args.on = {
          ready: function() {
            const flkty = this;

            // Setup custom dots
            if (custom_dots) {
              const dots = custom_dots.querySelectorAll('li');
              dots.forEach((dot, i) => {
                dot.addEventListener('click', () => flkty.select(i));
              });
              dots[this.selectedIndex].classList.add('is-selected');
            }
            
            // Setup autoplay progress
            if (autoplay && autoplay_progress && animations_enabled) {
              slideshow.setupAutoplayProgress(slideshow, autoplay_progress);
            }
          },
          change: function() {
            // Update custom dots
            if (custom_dots) {
              const dots = custom_dots.querySelectorAll('li');
              dots.forEach(dot => dot.classList.remove('is-selected'));
              dots[this.selectedIndex].classList.add('is-selected');
            }

            // Update autoplay progress
            if (autoplay && autoplay_progress && animations_enabled && slideshow.autoPlayProgressTL) {
              slideshow.autoPlayProgressTL.progress(0);
              if (this.player.state !== 'paused') {
                slideshow.autoPlayProgressTL.play();
              }
            }
          }
        };
      }

      // Product carousels
      if (slideshow.classList.contains('products')) {
        args.wrapAround = false;
        args.on.ready = function() {
          const flickity = this;
          if (next_buttons.length) {
            slideshow._resizeHandler = () => {
              slideshow.centerArrows(flickity, slideshow, prev_buttons[0], next_buttons[0]);
            };
            window.addEventListener('resize.center_arrows', slideshow._resizeHandler);
          }
          window.dispatchEvent(new Event('resize.center_arrows'));
        };
      }

      // Progress bar
      if (progress_bar) {
        args.wrapAround = false;
        args.on.scroll = function(progress) {
          progress = Math.max(0, Math.min(1, progress));
          progress_bar.style.width = progress * 100 + '%';
        };
      }

      // Initialize Flickity with optimized settings
      try {
        // Only initialize if Flickity is available
        if (typeof Flickity === 'undefined') {
          console.error('Flickity not loaded yet');
          return;
        }
        
        // Initialize with optimized settings
        const flkty = new Flickity(slideshow, args);
        
        // Optimize Flickity rendering - important for performance
        if (isMobile) {
          flkty.options.watchCSS = false;
          
          // Optimize image loading
          slideshow.querySelectorAll('img').forEach(img => {
            // Prioritize visible images
            if (img.closest('.is-initial-selected')) {
              img.loading = 'eager';
              if (img.dataset.src) {
                img.src = img.dataset.src;
                img.removeAttribute('data-src');
              }
            } else {
              img.loading = 'lazy';
            }
          });
        }
        
        slideshow.dataset.initiated = 'true';
        
        // Setup arrow button handlers
        if (prev_buttons) {
          prev_buttons.forEach(function(prev_button) {
            prev_button.addEventListener('click', () => {
              document.dir === 'rtl' ? flkty.next() : flkty.previous();
            });
            prev_button.addEventListener('keyup', event => {
              if (event.key === 'Enter' || event.key === ' ') {
                document.dir === 'rtl' ? flkty.next() : flkty.previous();
              }
            });
          });
          
          next_buttons.forEach(function(next_button) {
            next_button.addEventListener('click', () => {
              document.dir === 'rtl' ? flkty.previous() : flkty.next();
            });
            next_button.addEventListener('keyup', event => {
              if (event.key === 'Enter' || event.key === ' ') {
                document.dir === 'rtl' ? flkty.previous() : flkty.next();
              }
            });
          });
        }
        
        // Theme editor support
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          slideshow.addEventListener('shopify:block:select', event => {
            const index = slideshow_slides.indexOf(event.target);
            flkty.select(index);
          });
        }
        
        // Handle custom events
        this.addEventListener('change', this.reInit.bind(this));
        
      } catch (error) {
        console.error('Error initializing slideshow:', error);
      }
    }

    // Lazy initialization of animations to improve initial page load performance
    async initAnimations() {
      if (this.animationsReady) return Promise.resolve();
      
      const slideshow = this;

      // Ensure GSAP and SplitText are available
      if (typeof gsap === 'undefined' || typeof SplitText === 'undefined') {
        console.warn('GSAP or SplitText not available');
        return Promise.reject('GSAP or SplitText not available');
      }

      // Use SplitText more efficiently
      const textElements = slideshow.querySelectorAll('.slideshow__slide-heading, p:not(.subheading)');
      
      if (textElements.length === 0) {
        this.animationsReady = true;
        return Promise.resolve();
      }
      
      try {
        const splittext = new SplitText(textElements, {
          type: 'lines',
          linesClass: 'line-child'
        });
        
        const mask = new SplitText(textElements, {
          type: 'lines',
          linesClass: 'line-parent'
        });

        // Create animations for each slide
        slideshow.querySelectorAll('.slideshow__slide').forEach((item, i) => {
          const tl = gsap.timeline({
            paused: true
          });
          
          let button_offset = 0;
          this.animations[i] = tl;

          // Different transitions based on settings
          if (slideshow.dataset.transition == 'swipe') {
            tl.to(item, {
              duration: 0.7,
              clipPath: "polygon(100% 0, 0 0, 0 100%, 100% 100%)"
            }, "start");
          }

          if (slideshow.dataset.transition == 'zoom') {
            tl.to(item.querySelectorAll('.slideshow__slide-bg, .slideshow__slide-video-bg'), {
              duration: 1.5,
              scale: 1
            }, "start");
          }

          // Handle subheading
          const subheading = item.querySelector('.subheading');
          if (subheading) {
            tl.fromTo(subheading, {
              opacity: 0
            }, {
              duration: 0.5,
              opacity: 1
            }, 0);

            button_offset += 0.5;
          }

          // Handle heading
          const heading = item.querySelector('.slideshow__slide-heading');
          if (heading) {
            const lines = item.querySelectorAll('.slideshow__slide-heading .line-child');
            const h1_duration = Math.min(1 + ((lines.length - 1) * 0.1), 1.5); // Cap maximum duration
            
            tl.set(heading, {
              opacity: 1
            }, 0)
            .from(lines, {
              duration: h1_duration,
              yPercent: 120,
              stagger: 0.1
            }, 0);
            
            button_offset += h1_duration;
          }

          // Handle paragraph text
          const paragraph = item.querySelector('p.split-text');
          if (paragraph) {
            const lines = item.querySelectorAll('p:not(.subheading) .line-child');
            const p_duration = Math.min(1 + ((lines.length - 1) * 0.05), 1.2); // Cap maximum duration
            
            tl.set(paragraph, {
              opacity: 1
            }, 0)
            .from(lines, {
              duration: p_duration,
              yPercent: 120,
              stagger: 0.1,
            }, 0);
            
            button_offset += p_duration;
          }

          // Handle buttons
          const buttons = item.querySelectorAll('.button, .text-button');
          if (buttons.length) {
            tl.fromTo(buttons, {
              autoAlpha: 0
            }, {
              duration: 0.5,
              stagger: 0.1,
              autoAlpha: 1
            }, button_offset * 0.2);
          }

          // Store timeline reference
          item.dataset.timeline = tl;
        });

        this.animationsReady = true;
        return Promise.resolve();
      } catch (error) {
        console.error('Error initializing animations:', error);
        return Promise.reject(error);
      }
    }

    reInit() {
      const flickity = Flickity.data(this);
      if (flickity) {
        flickity.reloadCells();
      }
    }

    setupAutoplayProgress(slideshow, autoplay_progress) {
      if (!gsap) return;
      
      slideshow.autoPlayProgressTL = gsap.timeline({
        inherit: false
      });
      
      slideshow.autoPlayProgressTL
        .fromTo(autoplay_progress, {
          scaleX: 0
        }, {
          duration: parseInt(slideshow.dataset.autoplay, 10) / 1000,
          scaleX: 1,
          ease: 'linear'
        });
      
      const pauseProgress = () => {
        slideshow.autoPlayProgressTL.pause().progress(0);
      };
      
      const playProgress = () => {
        slideshow.autoPlayProgressTL.play();
      };
      
      slideshow.addEventListener('mouseenter', pauseProgress);
      slideshow.addEventListener('mouseleave', playProgress);
    }

    // Optimized video methods
    videoPause(video_container) {
      if (!video_container) return;
      
      const video = video_container.querySelector('video');
      if (!video) return;
      
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        try {
          video.pause();
        } catch (e) {
          console.error('Error pausing video:', e);
        }
      });
    }
    
    videoPlay(video_container) {
      if (!video_container) return;
      
      const video = video_container.querySelector('video');
      if (!video) return;
      
      requestAnimationFrame(() => {
        try {
          // Only play if video is not already playing
          if (video.paused) {
            // Use play() Promise API with error handling
            const playPromise = video.play();
            
            if (playPromise !== undefined) {
              playPromise.catch(error => {
                // Auto-play was prevented, handle silently
                console.log('Video autoplay prevented:', error);
              });
            }
          }
        } catch (e) {
          console.error('Error playing video:', e);
        }
      });
    }

    animateSlides(i, slideshow, animations) {
      if (!animations[i] || !slideshow.animationsReady) return;
      
      // Check if document.fonts is available and ready
      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          requestAnimationFrame(() => {
            animations[i].restart();
          });
        });
      } else {
        // Fallback if document.fonts is not available
        requestAnimationFrame(() => {
          animations[i].restart();
        });
      }
    }

    animateReverse(i, slideshow, animations) {
      if (!animations[i] || !slideshow.animationsReady) return;
      
      requestAnimationFrame(() => {
        animations[i].reverse();
      });
    }

    centerArrows(flickity, slideshow, prev_button, next_button) {
      if (!flickity || !flickity.cells || !flickity.cells.length) return;
      
      const first_cell = flickity.cells[0];
      let max_height = 0;
      let image_height = 0;
      
      // Get image height
      if (first_cell.element.querySelector('.product-featured-image')) {
        image_height = first_cell.element.querySelector('.product-featured-image').clientHeight;
      } else if (first_cell.element.querySelector('.gallery--item') && flickity.cells[1]) {
        image_height = flickity.cells[1].element.querySelector('.product-featured-image')?.clientHeight || 0;
      }
      
      if (image_height > 0) {
        // Find maximum cell height
        flickity.cells.forEach(item => {
          if (item.size.height > max_height) {
            max_height = item.size.height;
          }
        });

        if (max_height > image_height) {
          let difference = (max_height - image_height) / -2;
          
          // Apply transform
          requestAnimationFrame(() => {
            prev_button.style.transform = `translateY(${difference}px)`;
            next_button.style.transform = `translateY(${difference}px)`;
          });
        }
      }
    }
  }
  
  customElements.define('slide-show', SlideShow);
}